"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
exports.renderAndSplitTextComponent = renderAndSplitTextComponent;

var _preact = require("preact");

var _marked = require("marked");

var SVG_NS = 'http://www.w3.org/2000/svg';
/**
 * Replaces a related-article anchor's bare text content with a 3-part layout:
 *   [article icon] [title span] [chevron-right icon]
 *
 * Real DOM nodes (vs. CSS pseudo-element `background-image: url(data:...)`)
 * because the data-URL approach is fragile across CSP setups and a few of
 * our themes were rendering the chevron as blank. Inline SVGs styled via
 * `currentColor` always paint.
 *
 * Idempotent — the `.forethought-agent__related-articles-title` check skips
 * anchors that have already been decorated (e.g. when the DOMParser pass runs
 * twice during the split helper).
 */

function decorateRelatedArticleAnchor(anchor) {
  var _anchor$textContent;

  if (anchor.querySelector('.forethought-agent__related-articles-title')) return;
  var doc = anchor.ownerDocument;
  var text = ((_anchor$textContent = anchor.textContent) !== null && _anchor$textContent !== void 0 ? _anchor$textContent : '').trim();
  if (!text) return; // The visible title gets ellipsis-truncated to one line in the rail, so
  // expose the full string on `title=` for hover and `aria-label=` for
  // assistive tech. Both attributes are only set if the agent didn't already
  // provide one — that way an upstream override (e.g. a screen-reader label
  // distinct from the visible text) still wins.

  if (!anchor.hasAttribute('title')) anchor.setAttribute('title', text);
  if (!anchor.hasAttribute('aria-label')) anchor.setAttribute('aria-label', text);
  anchor.textContent = ''; // Leading article (document) icon.

  var articleIcon = doc.createElementNS(SVG_NS, 'svg');
  articleIcon.setAttribute('class', 'forethought-agent__related-articles-icon');
  articleIcon.setAttribute('viewBox', '0 0 24 24');
  articleIcon.setAttribute('width', '20');
  articleIcon.setAttribute('height', '20');
  articleIcon.setAttribute('fill', 'none');
  articleIcon.setAttribute('stroke', 'currentColor');
  articleIcon.setAttribute('stroke-width', '2');
  articleIcon.setAttribute('stroke-linecap', 'round');
  articleIcon.setAttribute('stroke-linejoin', 'round');
  articleIcon.setAttribute('aria-hidden', 'true');
  var docPath = doc.createElementNS(SVG_NS, 'path');
  docPath.setAttribute('d', 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z');
  var docPolyline = doc.createElementNS(SVG_NS, 'polyline');
  docPolyline.setAttribute('points', '14 2 14 8 20 8');
  var docLine1 = doc.createElementNS(SVG_NS, 'line');
  docLine1.setAttribute('x1', '8');
  docLine1.setAttribute('y1', '13');
  docLine1.setAttribute('x2', '16');
  docLine1.setAttribute('y2', '13');
  var docLine2 = doc.createElementNS(SVG_NS, 'line');
  docLine2.setAttribute('x1', '8');
  docLine2.setAttribute('y1', '17');
  docLine2.setAttribute('x2', '14');
  docLine2.setAttribute('y2', '17');
  articleIcon.appendChild(docPath);
  articleIcon.appendChild(docPolyline);
  articleIcon.appendChild(docLine1);
  articleIcon.appendChild(docLine2); // Title — wrapped in a span so flex sizing + ellipsis truncation can
  // target it (bare text nodes can't accept flex layout).

  var titleSpan = doc.createElement('span');
  titleSpan.className = 'forethought-agent__related-articles-title';
  titleSpan.textContent = text; // Trailing chevron-right.

  var chevron = doc.createElementNS(SVG_NS, 'svg');
  chevron.setAttribute('class', 'forethought-agent__related-articles-chevron');
  chevron.setAttribute('viewBox', '0 0 24 24');
  chevron.setAttribute('width', '16');
  chevron.setAttribute('height', '16');
  chevron.setAttribute('fill', 'none');
  chevron.setAttribute('stroke', 'currentColor');
  chevron.setAttribute('stroke-width', '2');
  chevron.setAttribute('stroke-linecap', 'round');
  chevron.setAttribute('stroke-linejoin', 'round');
  chevron.setAttribute('aria-hidden', 'true');
  var chevronPath = doc.createElementNS(SVG_NS, 'polyline');
  chevronPath.setAttribute('points', '9 18 15 12 9 6');
  chevron.appendChild(chevronPath);
  anchor.appendChild(articleIcon);
  anchor.appendChild(titleSpan);
  anchor.appendChild(chevron);
}
/**
 * Tags any `<ol>` / `<ul>` whose every `<li>` contains nothing but a single
 * hyperlink AND which is immediately preceded by a paragraph (the "label" —
 * e.g. "Related Articles:", "Sources:", "See also:"). Matches on shape rather
 * than the heading text so it's robust to wording changes across agents.
 *
 * Lists with mixed content (link + description, plain text bullets, etc.) and
 * link-lists with no preceding paragraph are left untouched.
 */


function tagLinkListSections(html) {
  if (typeof DOMParser === 'undefined') {
    return html;
  }

  var wrapper = new DOMParser().parseFromString("<div>".concat(html, "</div>"), 'text/html').body.firstChild;

  if (!wrapper) {
    return html;
  }

  wrapper.querySelectorAll('p').forEach(function (p) {
    var _p$textContent;

    if (!((_p$textContent = p.textContent) !== null && _p$textContent !== void 0 ? _p$textContent : '').trim()) {
      return;
    }

    var next = p.nextElementSibling;

    if (!next || next.tagName !== 'OL' && next.tagName !== 'UL') {
      return;
    }

    var items = Array.from(next.children).filter(function (c) {
      return c.tagName === 'LI';
    });

    if (items.length === 0) {
      return;
    }

    var allBareLinks = items.every(function (li) {
      // Keep element children + any text node with non-whitespace content.
      var meaningful = Array.from(li.childNodes).filter(function (n) {
        var _n$textContent;

        if (n.nodeType === Node.ELEMENT_NODE) return true;
        if (n.nodeType === Node.TEXT_NODE) return ((_n$textContent = n.textContent) !== null && _n$textContent !== void 0 ? _n$textContent : '').trim() !== '';
        return false;
      });
      return meaningful.length === 1 && meaningful[0].nodeType === Node.ELEMENT_NODE && meaningful[0].tagName === 'A';
    });

    if (allBareLinks) {
      next.classList.add('forethought-agent__related-articles');
      items.forEach(function (li) {
        var anchor = li.querySelector('a');
        if (!anchor) return;
        anchor.setAttribute('target', '_blank');
        anchor.setAttribute('rel', 'noopener noreferrer');
        decorateRelatedArticleAnchor(anchor);
      });
    }
  });
  return wrapper.innerHTML;
}

var TextComponent = function TextComponent(_ref) {
  var component = _ref.component,
      htmlOverride = _ref.htmlOverride;
  var html;

  if (htmlOverride !== undefined) {
    html = htmlOverride;
  } else {
    var raw = component.format === 'markdown' ? _marked.marked.parse(component.message) : component.message;
    html = tagLinkListSections(raw);
  }

  return (0, _preact.h)("div", {
    className: "forethought-agent__text",
    dangerouslySetInnerHTML: {
      __html: html
    }
  });
};
/**
 * Renders a TextComponent to HTML (markdown → HTML + link-list tagging) and
 * splits the result into:
 *   - `mainHtml`     — the body without "Related Articles:" label + UL.
 *   - `articlesHtml` — the related-articles UL (and any "Related Articles:"
 *                      style label paragraph immediately preceding it).
 *                      `null` if the body has no related-articles block.
 *
 * Used by AgentMessage so the related-articles cards can render in the
 * right-hand rail while the rest of the answer flows in the main left column.
 */


function renderAndSplitTextComponent(component) {
  var _prev$textContent, _list$parentNode;

  var raw = component.format === 'markdown' ? _marked.marked.parse(component.message) : component.message;
  var tagged = tagLinkListSections(raw);

  if (typeof DOMParser === 'undefined') {
    return {
      mainHtml: tagged,
      articlesHtml: null
    };
  }

  var wrapper = new DOMParser().parseFromString("<div>".concat(tagged, "</div>"), 'text/html').body.firstChild;

  if (!wrapper) {
    return {
      mainHtml: tagged,
      articlesHtml: null
    };
  }

  var list = wrapper.querySelector('.forethought-agent__related-articles');

  if (!list) {
    return {
      mainHtml: tagged,
      articlesHtml: null
    };
  } // Only the UL itself moves to the rail. If the previous sibling is a label
  // paragraph (e.g. "Related Articles:") we drop it entirely — the rail
  // doesn't render headings/titles for the cards.


  var articlesContainer = document.createElement('div');
  articlesContainer.appendChild(list.cloneNode(true));
  var prev = list.previousElementSibling;

  if (prev && prev.tagName === 'P' && ((_prev$textContent = prev.textContent) !== null && _prev$textContent !== void 0 ? _prev$textContent : '').trim().endsWith(':')) {
    var _prev$parentNode;

    (_prev$parentNode = prev.parentNode) === null || _prev$parentNode === void 0 ? void 0 : _prev$parentNode.removeChild(prev);
  }

  (_list$parentNode = list.parentNode) === null || _list$parentNode === void 0 ? void 0 : _list$parentNode.removeChild(list);
  return {
    mainHtml: wrapper.innerHTML,
    articlesHtml: articlesContainer.innerHTML
  };
}

var _default = TextComponent;
exports.default = _default;